import { useTranslation } from 'react-i18next'
import { Input, Select, Switch, type SelectOption } from '@ludus/ui'
import type { PipelineId } from '@ludus/core'
import type { NodeField } from '@/features/flows/types.ts'
import {
  boardForField,
  useMailTemplateChoices,
  useManagerChoices,
  usePipelineChoices,
  useStatusChoices,
  type PipelineChoice,
} from './useDrawerOptions.ts'
import { TemplateInput } from './TemplateInput.tsx'
import type { ChainPath } from './dataChain.ts'

/**
 * One option, drawn from its `NodeField` and nothing else (D5).
 *
 * This is the file that makes four drawers out of one component: the switch is
 * on `field.type`, which core declares, so a fifth node type needs a spec and
 * two locale rows and no code here. Nothing in this file names a node type, and
 * adding such a name would be re-opening the seam `NodeSpec.fields` exists to
 * close.
 *
 * `templatable` decides whether the value box accepts `{{ }}`; it is read off
 * the field rather than guessed, because identity fields (`targetStatusId`,
 * `owner`) are concrete ids validated at save *and* at run, so a template in
 * one is a validation issue rather than a runtime surprise (ADR-0007 §6).
 */

interface FieldControlProps {
  field: NodeField
  value: unknown
  options: Record<string, unknown>
  ownPipelineId: PipelineId
  paths: ChainPath[]
  /** Every pipeline except the flow's own — what a target picker may offer. */
  targetPipelines: PipelineChoice[]
  onChange: (value: unknown) => void
}

export function FieldControl({
  field,
  value,
  options,
  ownPipelineId,
  paths,
  targetPipelines,
  onChange,
}: FieldControlProps) {
  const { t } = useTranslation()
  const board = boardForField(field, options, ownPipelineId)
  const statuses = useStatusChoices(field.type === 'status' ? board : null)
  const managers = useManagerChoices(field.type === 'manager' ? board : null)
  const pipelines = usePipelineChoices()
  // Asked for only by the field that needs it — the hook's key is `null`
  // otherwise, so opening any other drawer costs no request.
  const mailTemplates = useMailTemplateChoices(field.type === 'mail_template')

  // A stored value as text. Numbers are included on purpose: `price` is
  // templatable, so its control is a text box, and collapsing `1200` to `''`
  // showed an empty Price for a card that had one — and left no way to type a
  // literal, since every keystroke would then be written back as a string the
  // `number` field refuses.
  const asString =
    typeof value === 'string' ? value : typeof value === 'number' ? String(value) : ''

  switch (field.type) {
    case 'boolean':
      return <Switch checked={value === true} onCheckedChange={(checked) => onChange(checked)} />

    case 'select':
      return (
        <Select
          value={asString}
          onValueChange={(next) => onChange(next)}
          options={(field.choices ?? []).map((choice) => ({
            value: choice,
            // Keyed by field *and* value, so a new choice is two locale rows
            // and no code — the same rule the canvas card reads by.
            label: t(`pages.flows.canvas.choice.${field.name}.${choice}`, {
              defaultValue: choice,
            }),
          }))}
        />
      )

    case 'pipeline':
      return (
        <Select
          value={asString}
          onValueChange={(next) => onChange(next)}
          placeholder={t('pages.flows.drawer.pick.pipeline', { defaultValue: 'Pick a board' })}
          options={(field.name === 'targetPipelineId' ? targetPipelines : pipelines.choices).map(
            (p) => ({ value: p.id, label: p.name }),
          )}
        />
      )

    case 'status': {
      const statusOptions: SelectOption[] = statuses.choices.map((s) => ({
        value: s.id,
        label: s.name,
        // D6: a flow-created card may never land on the distribution trigger.
        // Shown and refused, with the reason, rather than quietly absent.
        disabled: s.isDistributionTrigger,
        hint: s.isDistributionTrigger
          ? t('pages.flows.drawer.statusDistributes', {
              defaultValue: 'Distribution runs on this status, so a flow cannot land a card here.',
            })
          : undefined,
      }))
      return (
        <Select
          value={asString}
          onValueChange={(next) => onChange(next)}
          placeholder={
            board === null
              ? t('pages.flows.drawer.pick.boardFirst', { defaultValue: 'Pick a board first' })
              : t('pages.flows.drawer.pick.status', { defaultValue: 'Pick a status' })
          }
          options={statusOptions}
          disabled={board === null}
        />
      )
    }

    case 'manager':
      return (
        <Select
          value={asString}
          onValueChange={(next) => onChange(next)}
          placeholder={
            board === null
              ? t('pages.flows.drawer.pick.boardFirst', { defaultValue: 'Pick a board first' })
              : t('pages.flows.drawer.pick.manager', { defaultValue: 'Pick an owner' })
          }
          options={managers.choices.map((m) => ({ value: m.id, label: m.name }))}
          disabled={board === null}
        />
      )

    case 'number':
      // Templatable, so it cannot be an `<input type="number">`: `{{ $lead.price }}`
      // is a legal value here and a number input would refuse to hold it.
      return field.templatable ? (
        // Written back as a number when it reads as one, and as text only when
        // it does not — which is what makes a literal price legal and a
        // `{{ }}` template legal, without the box having to know which it is.
        <TemplateInput
          value={asString}
          paths={paths}
          onChange={(next) => onChange(asNumeric(next))}
        />
      ) : (
        <Input
          value={asString}
          inputMode="numeric"
          onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
        />
      )

    case 'tags': {
      // A list whose *entries* may be templates; the list itself is not one
      // template. Comma-separated in, array out, which keeps the option an
      // array at rest as well as after rendering.
      const asList = Array.isArray(value) ? value.map(String).join(', ') : ''
      return (
        <Input
          value={asList}
          placeholder={t('pages.flows.drawer.tagsHint', { defaultValue: 'tag, tag' })}
          onChange={(e) => {
            const entries = e.target.value
              .split(',')
              .map((s) => s.trim())
              .filter((s) => s !== '')
            // An emptied box returns the field to its own default rather than
            // always to `[]`. On `update_lead` the default is `null`, which
            // means *this option is not named* and leaves the target's tags
            // alone; writing `[]` there would make clearing the box an
            // instruction to wipe the sibling card's tags on every run, with
            // nothing in the drawer able to undo it.
            onChange(entries.length > 0 ? entries : ((field.default ?? null) as unknown))
          }}
        />
      )
    }

    case 'text':
      return field.templatable ? (
        <TemplateInput value={asString} paths={paths} onChange={onChange} />
      ) : (
        <Input value={asString} onChange={(e) => onChange(e.target.value)} />
      )

    case 'mail_template': {
      // A picker when the sink answers, a plain box when it cannot. The
      // fallback is not a nicety: a flow whose sink is briefly unreachable must
      // still be editable, and a value already stored must never be silently
      // unselectable because a list failed to load.
      if (!mailTemplates.configured || mailTemplates.failed) {
        return (
          <div className="space-y-1">
            <Input value={asString} onChange={(e) => onChange(e.target.value)} />
            <p className="text-muted-foreground text-xs">
              {mailTemplates.configured
                ? t('pages.flows.drawer.mail.unreachable', {
                    defaultValue: 'Could not reach the mail sender — type the mail’s name.',
                  })
                : t('pages.flows.drawer.mail.unconfigured', {
                    defaultValue: 'This instance has no mail sender configured.',
                  })}
            </p>
          </div>
        )
      }
      return (
        <Select
          value={asString}
          onValueChange={(next) => onChange(next)}
          placeholder={t('pages.flows.drawer.pick.mail', { defaultValue: 'Pick a mail' })}
          // The sink's own words, never translated: this console holds no
          // dictionary of mail names and must not grow one (#377).
          options={mailTemplates.choices.map((template) => ({
            value: template.name,
            label: template.label ?? template.name,
            hint: template.description,
          }))}
          disabled={mailTemplates.isLoading}
        />
      )
    }

    // `rule` is not drawn here: it has its own builder, and the drawer routes
    // it there before reaching this switch. Kept explicit so a new field type
    // is a compile error rather than a blank row.
    case 'rule':
      return null
  }
}

/**
 * A typed value for a templatable `number` field: a real number when the text
 * reads as one, the text itself otherwise (so a `{{ }}` template survives), and
 * `null` when the box is empty.
 */
function asNumeric(raw: string): number | string | null {
  const trimmed = raw.trim()
  if (trimmed === '') return null
  const n = Number(trimmed)
  return Number.isFinite(n) ? n : raw
}
